import { useState } from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import { SkeletonCards, tokens } from '@agribot/polygon-checks-ui'
import { useDelete, useDeletionPreview, type DeletionKind, type DeletionTarget } from '../api'

/** What each count in a preview means, by kind. A count the API adds later shows under its own key. */
const REMOVES: Record<DeletionKind, Record<string, string>> = {
  campaign: {
    assignments: 'material assignments', submissions: 'submissions', answers: 'material answers', documents: 'documents',
    reviewDecisions: 'review decisions', flags: 'review flags', reminders: 'reminders sent', exports: 'exports',
  },
  supplier: {
    submissions: 'submissions, with their answers', materials: 'materials', assignments: 'campaign assignments', documents: 'documents',
    reviewers: 'reviewer assignments', helpRequests: 'help requests', reminders: 'reminders sent', logins: 'supplier logins that act only for it',
    ownOrigins: 'origins it owns', farmLinks: 'farm links it made',
  },
  parent: {},
  assignment: {
    assignments: 'material assignments in this campaign', submissions: 'submission in this campaign', answers: 'material answers',
    documents: 'documents', reviewDecisions: 'review decisions', flags: 'review flags', reminders: 'reminders sent',
  },
}
const KEEPS: Record<DeletionKind, Record<string, string>> = {
  campaign: {
    suppliers: 'suppliers, with their materials, origins, farms and plots',
    helpRequests: 'help requests, kept with their supplier',
  },
  supplier: {
    campaigns: 'campaigns it was in (for the other suppliers)', groupOrigins: 'origins its group owns',
    plots: 'plots linked to it (the link is cleared)', regions: 'plot workspaces, handed to your organisation',
    loginsForOtherSuppliers: 'logins that also act for other suppliers',
  },
  parent: {
    suppliersMovedUp: 'legal entities move up to the company above it', suppliersStandalone: 'legal entities become standalone',
    originsMovedUp: 'origins move up to the company above it',
  },
  assignment: { materials: 'materials, to assign again later', logins: 'supplier logins' },
}
const ALWAYS_KEPT: Record<DeletionKind, string> = {
  campaign: 'The taxonomy, suppliers, origins, farm records and plots stay. Usage already recorded for billing stays too.',
  supplier: 'Farmer organisations, farmers and plots stay as your organisation’s farm records. Usage already recorded for billing stays too.',
  parent: 'Nothing else is removed.',
  assignment: 'The supplier stays, with its origins, farm records and its other campaigns. It is not emailed.',
}
const NOUN: Record<DeletionKind, string> = { campaign: 'campaign', supplier: 'supplier', parent: 'parent company', assignment: 'supplier' }

function Counts({ title, counts, labels }: { title: string; counts: Record<string, number>; labels: Record<string, string> }) {
  const rows = Object.entries(counts).filter(([, n]) => n > 0)
  if (!rows.length) return null
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <Box component="ul" sx={{ m: 0, pl: 4, font: `400 14px ${tokens.font.sans}` }}>
        {rows.map(([k, n]) => <li key={k}><Box component="span" className="tnum" sx={{ fontWeight: 500 }}>{n}</Box> {labels[k] ?? k}</li>)}
      </Box>
    </Box>
  )
}

/**
 * Asks before deleting a campaign, a supplier or a parent company: what goes
 * and what stays, from the API's preview; the name typed when answered work
 * would be lost; the API's reason when it refuses.
 */
export function DeleteDialog({ target, onClose, onDeleted }: { target: DeletionTarget; onClose: () => void; onDeleted?: () => void }) {
  const { data: p, isPending, error } = useDeletionPreview(target)
  const remove = useDelete()
  const [typed, setTyped] = useState('')
  const noun = NOUN[target.kind]
  const nameOk = !p?.confirmByName || typed.trim() === p.name.trim()
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {target.kind === 'assignment' ? `Remove ${p ? `“${p.name}”` : 'the supplier'} from this campaign?` : `Delete ${noun}${p ? ` “${p.name}”` : ''}?`}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: '8px !important' }}>
        {isPending && <SkeletonCards columns="1fr" />}
        {error && <Alert severity="error">{(error as Error).message}</Alert>}
        {p && p.blocked && <Alert severity="warning">{p.blocked}</Alert>}
        {p && !p.blocked && (
          <>
            <Counts title="This removes" counts={p.removes} labels={REMOVES[target.kind]} />
            {!Object.values(p.removes).some((n) => n > 0) && target.kind !== 'parent' && (
              <Typography variant="body2">Nothing else is attached to it.</Typography>
            )}
            <Counts title={target.kind === 'parent' ? 'What happens to what is in it' : 'This keeps'} counts={p.keeps} labels={KEEPS[target.kind]} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{ALWAYS_KEPT[target.kind]} It cannot be undone.</Typography>
            {p.confirmByName && (
              <TextField
                label={`Type “${p.name}” to confirm`}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                InputLabelProps={{ shrink: true }}
              />
            )}
          </>
        )}
        {remove.isError && <Alert severity="error">{(remove.error as Error).message}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
        <Button variant="text" onClick={onClose}>{p?.blocked ? 'Close' : 'Cancel'}</Button>
        {p && !p.blocked && (
          <Button
            color="error"
            disabled={!nameOk || remove.isPending}
            onClick={() => remove.mutate(target, { onSuccess: () => { onClose(); onDeleted?.() } })}
          >
            {target.kind === 'assignment'
              ? (remove.isPending ? 'Removing…' : 'Remove from campaign')
              : (remove.isPending ? 'Deleting…' : `Delete ${noun}`)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
